/**
 * ApplicantDashboard — refreshed dashboard composed entirely from
 * the (m.6) primitives.
 *
 * IA (PR (m.7)):
 *
 *   1. PageHeader (welcome + season eyebrow)
 *   2. Countdown hero (anchor event from useCountdownAnchor)
 *   3. KPI strip — Days to Pageant / Profile Completion / Documents /
 *      Service Hours
 *   4. Submission status panel — single composed card with title row,
 *      status pill, copy, and next-action CTA. Replaces the legacy
 *      DraftCard / SubmittedCard / ApprovedCard / RejectedCard family.
 *   5. Upcoming events + Recent activity row
 *   6. Scholarship accrual ChartCard — only rendered for approved
 *      contestants who actually have scholarship data
 *   7. Footer row — Instagram link CTA (if not linked) + Podium teaser
 *
 * Spec: meridian-redesign — PR (m.7) Dashboard refresh
 * Requirements: 1.5, 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 14.1, 18.5
 */

import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Calendar,
  ClipboardList,
  Clock,
  Crown,
  FileText,
  HelpCircle,
  MapPin,
  Shirt,
  Sparkles,
} from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { useApprovalAccess } from '@/hooks/useApprovalAccess';
import { useApplicantDashboard, type ApplicantDashboardYearProfile } from '@/hooks/useApplicantDashboard';
import { useCountdownAnchor } from '@/hooks/useCountdownAnchor';
import { useVault } from '@/hooks/useVault';
import { deriveGreetingFirstName } from '@/utils/greeting';
import { shortenAddress } from '@/utils/shortenAddress';
import { cn } from '@/lib/utils';
import { computeRemaining } from '@/lib/countdown';
import { formatDeadlineLocal } from '@/lib/phaseDeadlineFormat';

import { PageHeader } from '@/components/ui/page-header';
import { Countdown } from '@/components/ui/countdown';
import { KpiTile } from '@/components/ui/kpi-tile';
import { StatusPill, type StatusPillVariant } from '@/components/ui/status-pill';
import { ChartCard, ChartFrame } from '@/components/ui/chart-card';

import { InstagramIcon } from '@/components/ui/InstagramIcon';
import { isApplicationFormEnabled } from '@/config/featureFlags';
import { useActiveIntakeSeasons } from '@/hooks/useActiveIntakeSeasons';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import { resolveActiveCompetitionId } from '@/lib/application-form/activeCompetition';
import { useApplicantApplicationState } from '@/components/application-form/hooks/useApplicantApplicationState';
import { useApplicationDivisions } from '@/components/application-form/hooks/useApplicationDivisions';
import { useApplicationDashboardCompletion } from '@/components/application-form/hooks/useApplicationDashboardCompletion';
import { ApplicantJourneyCard } from './ApplicantJourneyCard';
import {
  SeasonTransitionNotice,
  shouldShowSeasonTransitionNotice,
} from './SeasonTransitionNotice';

import { Area, AreaChart } from 'recharts';

// ---------------------------------------------------------------------------
// Status panel — replaces the legacy DraftCard/SubmittedCard/Approved/Rejected
// ---------------------------------------------------------------------------

interface StatusPanelDescriptor {
  title: string;
  body: string;
  pillVariant: StatusPillVariant;
  pillLabel: string;
  cta?: { label: string; to: string };
}

function describeStatusPanel(
  profile: ApplicantDashboardYearProfile | null,
  /** Operative competition year (from the active-season seam) for copy. */
  operativeYear: number,
  /**
   * True when the status is driven by the new director-form ApplicationResponse
   * flow (no legacy ApplicantYearProfile). In that flow both Phase 1 and Phase 2
   * live on the `/apply` surface, so the continue/open CTAs point there rather
   * than at the legacy `/portal/profile` sections.
   */
  isNewFlow = false,
): StatusPanelDescriptor {
  // The application-editing surface: `/apply` for the new director-form flow,
  // the legacy profile sections otherwise.
  const applyTo = isNewFlow ? '/apply' : '/portal/profile';
  const phase2To = isNewFlow ? '/apply' : '/portal/profile?section=stage-form';

  // No record yet — first-time visitor.
  if (!profile) {
    return {
      title: 'Start Your Application',
      body: `Welcome to the ${operativeYear} season. Begin Phase 1 to introduce yourself to the pageant team.`,
      pillVariant: 'neutral',
      pillLabel: 'Not Started',
      cta: { label: 'Start Application', to: applyTo },
    };
  }

  switch (profile.submissionStatus) {
    case 'DRAFT':
      return {
        title: 'Phase 1 — Application',
        body:
          'Your Phase 1 application is in progress. Continue editing or ' +
          'submit when you are ready for the pageant team to review it.',
        pillVariant: 'pending',
        pillLabel: 'Draft',
        cta: { label: 'Continue Application', to: applyTo },
      };
    case 'SUBMITTED':
      return {
        title: 'Application Under Review',
        body:
          'Your Phase 1 application has been received. The pageant team ' +
          'will reach out with next steps once review is complete.',
        pillVariant: 'info',
        pillLabel: 'Under Review',
      };
    case 'APPROVED':
      return {
        title: 'Phase 2 — Competition Forms',
        body:
          'Congratulations, you\u2019ve been approved! Complete your Phase 2 ' +
          'competition forms to finish your entry.',
        pillVariant: 'approved',
        pillLabel: 'Approved',
        cta: { label: 'Open Phase 2', to: phase2To },
      };
    case 'REJECTED':
      return {
        title: 'Application Not Approved',
        body:
          'Your application was not approved this season. Please reach ' +
          'out to the pageant director if you have questions.',
        pillVariant: 'rejected',
        pillLabel: 'Not Approved',
      };
    default:
      return {
        title: 'Application Status',
        body: 'We could not determine the current status of your application.',
        pillVariant: 'neutral',
        pillLabel: profile.submissionStatus,
      };
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ApplicantDashboardProps {
  /**
   * Optional pre-fetched dashboard data. When provided, the component
   * renders this snapshot directly and skips its data hook — used by
   * the dev preview at `/dev/dashboards-preview` to mount the
   * dashboard without a live AWS session. Production usage omits
   * this prop and the live hook fetches the real data.
   */
  previewData?: import('@/hooks/useApplicantDashboard').ApplicantDashboardData;
  /**
   * Optional override for the countdown anchor. The preview page
   * supplies a deterministic anchor so the rendered dashboard is
   * stable across reloads.
   */
  previewCountdownDate?: string;
  /**
   * Optional override for the contestant's first-name greeting.
   * Defaults to the authenticated user's name when omitted.
   */
  previewFirstName?: string;
  /**
   * Optional injected reference clock (epoch ms) for the Phase 2 countdown.
   * When provided the countdown evaluates against this fixed instant instead
   * of the live ticking clock — used by tests to pin the countdown on either
   * side of the Phase_2_Due_Date. Production usage omits it.
   */
  nowMs?: number;
}

export function ApplicantDashboard(props: ApplicantDashboardProps = {}): JSX.Element {
  const { user, isDirector } = useAuth();
  if (isDirector && !props.previewData) return <Navigate to="/admin" replace />;

  // Season SSOT: operative competition year (and the resolved Season, which
  // carries the Phase_2_Due_Date) from the active-season seam.
  const { season, operativeYear } = useActiveSeason();

  const liveDashboard = useApplicantDashboard({
    cognitoSub: user?.cognitoSub,
    email: user?.email,
  });
  const liveAnchor = useCountdownAnchor(operativeYear);
  const { data: vaultData } = useVault();

  // Approval gating derives from the Canonical_Approval_Reader
  // (approval-status-ssot-unification Req 4.2): the events feed, Phase 2
  // checklist / countdown, and scholarship-accrual card unlock iff this hook
  // resolves `full` — i.e. the contestant's Approval_SSOT
  // (`ApplicationResponse.submissionStatus`) is APPROVED. Loading/error resolve
  // to a non-`full` state, so those surfaces stay locked until approval is
  // confirmed (Req 4.10), and no legacy `ApplicantYearProfile.submissionStatus`
  // read gates them (Req 4.11).
  const { state: approvalState } = useApprovalAccess();

  // Flag-on intake-window gate (member-onboarding-flow Req 3.1, 3.2, 3.6):
  // the Active_Intake_Season set decides whether an application/profile control
  // is reachable from the dashboard. The clock is injected inside the hook at
  // the I/O boundary; the set is recomputed on each resolution (Req 3.5, 6.5).
  const activeIntake = useActiveIntakeSeasons();

  // The applicant's new-flow ApplicationResponse state (shared resolver, same
  // read the /apply surface and the journey card use). This is the SSOT for the
  // director-form flow, which writes NO legacy ApplicantYearProfile — so an
  // approved contestant has empty `yearProfiles` and the dashboard must derive
  // their status from here to render the full "Journey" layout rather than the
  // single start card.
  const applyCompetitionId = resolveActiveCompetitionId(operativeYear, user?.activeTenantId);
  const appState = useApplicantApplicationState({
    competitionId: applyCompetitionId,
    intakeOpen: activeIntake.seasons.length > 0,
    hasPublishedForm: true,
    selectedDivisionKey: null,
  });

  // Live "application completion" for the director-form flow, computed from the
  // published form's phase-gated pages + the applicant's answers. It recomputes
  // as Phase_2 pages unlock on approval, so the completion KPI updates
  // dynamically instead of reporting a static value.
  const { divisions: applyDivisions } = useApplicationDivisions(operativeYear);
  const { completion: liveCompletion } = useApplicationDashboardCompletion({
    competitionId: applyCompetitionId,
    response: appState.response,
    activeDivisions: applyDivisions,
    enabled: !props.previewData && !!appState.response,
  });

  // Choose preview data over live fetches when injected.
  const data = props.previewData ?? liveDashboard.data;
  const loading = props.previewData ? false : liveDashboard.loading;
  const error = props.previewData ? null : liveDashboard.error;
  const countdownAnchor = props.previewCountdownDate
    ? { date: props.previewCountdownDate, title: 'Pageant Day' }
    : liveAnchor.anchor;

  const firstName = props.previewFirstName ?? deriveGreetingFirstName(user);

  // Days remaining computed once for the KPI tile (Countdown does its
  // own ticker; the tile uses the static at-render value).
  const daysToPageant = useMemo(() => {
    if (!countdownAnchor?.date) return null;
    const diffMs = new Date(countdownAnchor.date).getTime() - Date.now();
    return Math.max(0, Math.ceil(diffMs / 86_400_000));
  }, [countdownAnchor?.date]);

  // Scholarships awarded to this contestant; the chart only renders
  // when there is at least one entry.
  const myScholarships = useMemo(() => {
    if (!vaultData?.scholarships?.length || !data?.applicant?.id) return [];
    return vaultData.scholarships.filter(
      (s) => s.awardedTo === data.applicant!.id && s.awardedAt,
    );
  }, [vaultData?.scholarships, data?.applicant?.id]);

  const accrualSeries = useMemo(() => {
    if (myScholarships.length === 0) return [];
    const sorted = [...myScholarships].sort(
      (a, b) => (a.awardedAt ?? '').localeCompare(b.awardedAt ?? ''),
    );
    let running = 0;
    return sorted.map((s) => {
      running += s.amount ?? 0;
      const month = s.awardedAt
        ? new Date(s.awardedAt).toLocaleDateString('en-US', {
            month: 'short',
            year: '2-digit',
          })
        : '';
      return { month, value: running };
    });
  }, [myScholarships]);

  if (loading) {
    return (
      <div
        className="text-center py-16 px-5"
        data-testid="dashboard-loading"
      >
        <p className="text-muted-foreground text-lg">Loading your dashboard…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="text-center py-16 px-5"
        data-testid="dashboard-error"
      >
        <div className="bg-destructive/10 border border-destructive text-destructive rounded-lg p-4 inline-block">
          <p>Failed to load your dashboard. Please refresh the page.</p>
        </div>
      </div>
    );
  }

  const { applicant, currentProfile, docCount, serviceHours, instagramLinked, upcomingEvents, profileCompletion } = data!;

  // ---------------------------------------------------------------------------
  // Flag-on intake-window gate (member-onboarding-flow Req 3.1, 3.2, 3.6, 6.3,
  // 6.4; refined by apply-season-auto-derivation Req 1.1–1.4, 4.1, 4.4). A
  // Person who holds no Participation for any Season no longer sees a dead-end
  // "choose a Season" panel. Instead the application surface is reachable ONLY
  // while an intake window is open — as a SINGLE "Begin Your Application" control
  // (linking to `/apply` with NO `competitionId`; the season is derived
  // downstream, never chosen here) whenever the Active_Intake_Season set is
  // non-empty, and NO application control at all when the active set is empty.
  // The calendar and member surfaces stay reachable via the surrounding portal
  // layout regardless of intake windows. The dev preview (`previewData`) is
  // intentionally exempt so it can mount any state.
  const hasNoParticipation =
    !applicant || (applicant.yearProfiles?.length ?? 0) === 0;
  if (isApplicationFormEnabled() && !props.previewData && hasNoParticipation) {
    // Don't flash a no-control state while the Season OR the application-state
    // read is still in flight — wait for resolution before deciding (Req 3.5,
    // 6.5 recompute-on-resolution).
    if (activeIntake.status === 'loading' || appState.loading) {
      return (
        <div
          className="text-center py-16 px-5"
          data-testid="dashboard-loading"
        >
          <p className="text-muted-foreground text-lg">Loading your dashboard…</p>
        </div>
      );
    }
    // Only the genuinely pre-application states get the single start/journey
    // card. Once the contestant has an in-flight or decided ApplicationResponse
    // (the new director-form flow, which creates no legacy ApplicantYearProfile),
    // fall through to the FULL dashboard driven by that response's status — this
    // restores the rich "Journey to the Crown" layout for approved contestants
    // instead of stranding them on the single "You're approved" card.
    const kind = appState.state.kind;
    const startedNewFlow =
      kind === 'IN_PROGRESS' ||
      kind === 'SUBMITTED' ||
      kind === 'CHANGES_REQUESTED' ||
      kind === 'APPROVED';
    if (!startedNewFlow) {
      return <ApplicantJourneyCard firstName={firstName} nowMs={props.nowMs} />;
    }
  }

  // The status source: the legacy ApplicantYearProfile when one exists, else a
  // minimal profile synthesized from the new-flow ApplicationResponse (its
  // submission status + director-approved title) so the status panel, title row,
  // and approved-only sections render for a director-form contestant who has no
  // AYP. `usingNewFlow` routes the Phase 1/2 CTAs to `/apply`.
  const usingNewFlow = !props.previewData && !currentProfile && !!appState.response;
  const effectiveProfile: ApplicantDashboardYearProfile | null =
    currentProfile ??
    (!props.previewData && appState.response
      ? {
          applicantId: applicant?.id ?? '',
          competitionYear: operativeYear,
          applicantType: '',
          submissionStatus: appState.response.submissionStatus,
          profileLocked: false,
          placementQuestionsLocked: false,
          requestedTitle: appState.response.titlePreferences?.[0] ?? null,
          approvedTitle: appState.response.approvedTitle ?? null,
          titleApproved: Boolean(appState.response.approvedTitle),
          directorNotes: null,
          phase1Data: null,
        }
      : null);

  const status = describeStatusPanel(
    effectiveProfile,
    operativeYear,
    usingNewFlow,
  );
  // Approval-unlocked condition. In the live app this derives from the
  // Canonical_Approval_Reader (the Approval_SSOT), so it agrees with every
  // other approval-gating surface and cannot lag the director's decision
  // (Req 4.2, 4.11). In the dev-preview harness (`previewData`) — which mounts
  // a deterministic snapshot without a live session — approval follows the
  // injected profile's status, consistent with how `data`/`loading`/`error`
  // are sourced above.
  const isApproved = props.previewData
    ? effectiveProfile?.submissionStatus === 'APPROVED'
    : approvalState === 'full';

  // Season_Transition_Notice (season-phase-deadlines R15): once the active
  // Season's Phase_1_Close_Date (`intakeWindow.closesAt`) has been reached, a
  // prospective Applicant — one with no SUBMITTED/APPROVED/REJECTED Application
  // for that Season (i.e. no record or a DRAFT) — sees the transition notice IN
  // PLACE OF the open Phase 1 application affordance, which prevents initiating a
  // Phase 1 submission (R15.1, R15.4). The close-side decision reuses the shared
  // pure `isDeadlineReached` via `shouldShowSeasonTransitionNotice`; the clock is
  // injectable via `props.nowMs` (tests), else live. No notice when Phase 1 is
  // absent or still open (R15.5).
  const transitionNow =
    props.nowMs !== undefined ? new Date(props.nowMs) : new Date();
  const showSeasonTransitionNotice =
    !props.previewData &&
    shouldShowSeasonTransitionNotice({
      season,
      submissionStatus: effectiveProfile?.submissionStatus ?? null,
      now: transitionNow,
    });
  // Completion KPI source: for the director-form flow, the LIVE page-level
  // completion computed from the published form (Phase 1 + any unlocked Phase 2)
  // — so it updates as Phase 2 pages become available and get filled. Falls back
  // to the AYP fragment count for the legacy flow (and briefly, before the live
  // form resolves, to that same count which is 0 for a director-form contestant).
  //
  // Prefer `liveCompletion` whenever it has resolved, rather than gating on
  // `usingNewFlow`. `usingNewFlow` assumes a director-form-only applicant has NO
  // AYP-equivalent `currentProfile` (`!currentProfile`) — but `listApplicantYearProfiles`
  // now projects that profile from `Participation`, which is the universal SSOT
  // for every applicant, so `currentProfile` is non-null even for someone with no
  // legacy phase1Data/stageFormData/etc. That silently routed those applicants to
  // the legacy AYP-fragment count, which finds none of its five fragments and
  // reports 0% regardless of how complete their real (new-flow) application is.
  // `liveCompletion` is already gated on the real signal — `enabled: !!appState.response`
  // in `useApplicationDashboardCompletion` — so preferring it whenever resolved is
  // correct for both flows and doesn't touch `usingNewFlow`'s other use (CTA routing).
  const effectiveCompletion = liveCompletion ?? profileCompletion;
  const visibleEvents = isApproved
    ? upcomingEvents
    : upcomingEvents.filter((e) => e.type === 'Public');

  return (
    <div className="space-y-10" data-testid="applicant-dashboard">
      <PageHeader
        eyebrow={`${operativeYear} Season · Welcome back, ${firstName}`}
        size="lg"
      >
        Your Journey to the Crown
      </PageHeader>

      {/* Countdown hero */}
      {countdownAnchor?.date ? (
        <Countdown
          targetDate={countdownAnchor.date}
          eyebrow={countdownAnchor.title || 'Pageant Day'}
        />
      ) : null}

      {/* KPI strip */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="dashboard-kpis"
      >
        <KpiTile
          label="Days to Pageant"
          value={daysToPageant !== null ? daysToPageant : '—'}
          variant="tinted"
          caption={countdownAnchor?.title}
          data-testid="kpi-days"
        />
        <KpiTile
          label="Profile Completion"
          value={`${effectiveCompletion.percent}%`}
          caption={`${effectiveCompletion.completed} of ${effectiveCompletion.total} sections`}
          data-testid="kpi-profile"
        />
        <KpiTile
          label="Documents"
          value={docCount}
          caption={docCount === 0 ? 'No uploads yet' : `${docCount} uploaded`}
          data-testid="kpi-documents"
        />
        <KpiTile
          label="Service Hours"
          value={serviceHours.toLocaleString(undefined, {
            maximumFractionDigits: 1,
          })}
          caption={serviceHours === 0 ? 'Log your first activity' : 'Year to date'}
          data-testid="kpi-service"
        />
      </div>

      {/* Phase 2 due-date countdown (season-phase-deadlines Task 14).
          Rendered ABOVE the Phase 2 "Competition Forms" status panel, ONLY for
          an APPROVED Application whose Season carries a Phase_2_Due_Date — Phase
          2 only; there is no Phase 1 dashboard countdown (design OQ6). The
          sub-component shows the live countdown before the due date and the
          "Phase 2 closed" message at/after it. */}
      {isApproved && season?.phaseTwoDueDate ? (
        <PhaseTwoCountdown dueDate={season.phaseTwoDueDate} now={props.nowMs} />
      ) : null}

      {/* Status panel */}
      <div
        className="bg-card border border-border rounded-card shadow-subtle p-6 sm:p-8"
        data-testid="status-panel"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-display text-page-title text-foreground">
                {status.title}
              </h2>
              <StatusPill variant={status.pillVariant}>
                {status.pillLabel}
              </StatusPill>
            </div>
            {effectiveProfile?.requestedTitle || effectiveProfile?.approvedTitle ? (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-eyebrow-sm text-muted-foreground">
                  Title
                </span>
                <span className="font-sans text-sm text-foreground">
                  {effectiveProfile.approvedTitle || effectiveProfile.requestedTitle}
                </span>
                <StatusPill
                  variant={effectiveProfile.titleApproved ? 'approved' : 'pending'}
                >
                  {effectiveProfile.titleApproved ? 'Title Approved' : 'Title Pending'}
                </StatusPill>
              </div>
            ) : null}
            <p className="font-sans text-body text-muted-foreground max-w-prose">
              {status.body}
            </p>
            {effectiveProfile?.directorNotes ? (
              <div className="rounded-card border border-warning/30 bg-warning-muted/40 p-4">
                <span className="text-eyebrow-sm text-warning-text block mb-1">
                  Director Notes
                </span>
                <p className="font-sans text-sm text-foreground">
                  {effectiveProfile.directorNotes}
                </p>
              </div>
            ) : null}
          </div>
          {status.cta && !showSeasonTransitionNotice ? (
            <Link
              to={status.cta.to}
              className="text-eyebrow-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors px-5 py-3 min-h-[var(--touch-target-min)] inline-flex items-center rounded-card no-underline"
              data-testid="status-panel-cta"
            >
              {status.cta.label}
            </Link>
          ) : null}
        </div>

        {/* The legacy per-form Phase 2 checklist reads AYP fragment data and
            links to the legacy `/portal/profile` sections, so it only applies to
            the legacy flow. New-flow (director-form) contestants reach Phase 2
            through the status panel's "Open Phase 2" CTA → `/apply`. */}
        {isApproved && currentProfile ? <Phase2Checklist profile={currentProfile} /> : null}
      </div>

      {/* Season_Transition_Notice (season-phase-deadlines Task 16, R15). Shown
          in place of the open Phase 1 application affordance to a prospective
          Applicant once the active Season's Phase_1_Close_Date has been reached;
          the status-panel Phase 1 CTA above is suppressed while it renders. */}
      {showSeasonTransitionNotice ? (
        <SeasonTransitionNotice
          season={season}
          submissionStatus={effectiveProfile?.submissionStatus ?? null}
        />
      ) : null}

      {/* Upcoming events + recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <UpcomingEventsPanel events={visibleEvents} />
        <RecentActivityPanel
          docCount={docCount}
          serviceHours={serviceHours}
          submissionStatus={effectiveProfile?.submissionStatus}
        />
      </div>

      {/* Scholarship accrual — only for approved contestants with data */}
      {isApproved && accrualSeries.length > 0 ? (
        <ChartCard
          eyebrow="Scholarship Accrual"
          title={`${(accrualSeries[accrualSeries.length - 1]?.value ?? 0).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          })} awarded to date`}
        >
          <ChartFrame>
            <AreaChart data={accrualSeries}>
              <defs>
                <linearGradient id="accrual-gradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                dataKey="value"
                stroke="var(--primary)"
                strokeWidth={2}
                fill="url(#accrual-gradient)"
              />
            </AreaChart>
          </ChartFrame>
        </ChartCard>
      ) : null}

      {/* Instagram link CTA — only when we have an applicant + not linked */}
      {applicant && currentProfile && !instagramLinked ? (
        <Link
          to="/portal/profile?section=instagram"
          className="block bg-card border border-border rounded-card shadow-subtle p-5 hover:border-primary transition-colors no-underline"
          data-testid="instagram-link-cta"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
              <InstagramIcon className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <span className="text-eyebrow-sm text-muted-foreground">
                Pageant Instagram
              </span>
              <p className="font-sans text-sm text-foreground mt-0.5">
                Link your pageant Instagram to enable Insight tracking and
                impact reporting.
              </p>
            </div>
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
        </Link>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * PhaseTwoCountdown — the Contestant_Dashboard Phase 2 due-date countdown
 * (season-phase-deadlines Task 14; design OQ6).
 *
 * The parent renders this ONLY when the Application is APPROVED and its Season
 * has a `phaseTwoDueDate` present, so this component's sole job is the
 * before/after-instant decision:
 *
 *   - `now < dueDate`  → live countdown to the Phase_2_Due_Date, reusing the
 *     shared pure `computeRemaining` time-math (the same helper the pageant-day
 *     `CountdownBanner` uses) and presenting the underlying instant via the
 *     shared viewer-local `formatDeadlineLocal`.
 *   - `now >= dueDate` → the "Phase 2 closed" message in place of the countdown
 *     (exclusive at the instant, matching the server-side deadline semantics).
 *
 * The clock is injectable: when `now` is supplied (tests) the countdown
 * evaluates against that fixed instant and does not tick; otherwise it advances
 * once per second via `setInterval`, mirroring the `CountdownBanner` ticker.
 *
 * Requirements: 9.3, 12.1, 12.2, 12.3, 12.4, 12.5
 */
function PhaseTwoCountdown({
  dueDate,
  now,
}: {
  /** The Season's Phase_2_Due_Date — an absolute ISO-8601 instant. */
  dueDate: string;
  /** Optional injected reference clock (epoch ms). Live ticker when omitted. */
  now?: number;
}): JSX.Element | null {
  // Live 1s ticker for production; skipped entirely when a clock is injected.
  const [liveNow, setLiveNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (now !== undefined) return;
    const id = setInterval(() => setLiveNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [now]);
  const nowMs = now ?? liveNow;

  const dueMs = useMemo(() => {
    const t = new Date(dueDate).getTime();
    return Number.isNaN(t) ? null : t;
  }, [dueDate]);

  // Unparseable due date — render nothing (an absent/invalid deadline shows no
  // countdown). The parent already gates on presence; this is defensive.
  if (dueMs === null) return null;

  const formatted = formatDeadlineLocal(dueDate, { withZone: true });

  // Exclusive at the instant: now === due ⇒ closed (mirrors server semantics).
  if (nowMs >= dueMs) {
    return (
      <section
        className="bg-card border border-border rounded-card shadow-subtle p-6 sm:p-8"
        data-testid="phase2-countdown-closed"
      >
        <span className="text-eyebrow-sm text-muted-foreground">
          Phase 2
        </span>
        <p className="font-display text-page-title text-foreground mt-1">
          Phase 2 closed
        </p>
        <p className="font-sans text-body text-muted-foreground mt-2 max-w-prose">
          The Phase 2 submission deadline
          {formatted ? ` (${formatted})` : ''} has passed. Contact the pageant
          director if you have questions.
        </p>
      </section>
    );
  }

  const { days, hours, minutes, seconds } = computeRemaining(dueMs, nowMs);
  const units: ReadonlyArray<{ key: string; value: number; label: string }> = [
    { key: 'days', value: days, label: 'Days' },
    { key: 'hours', value: hours, label: 'Hours' },
    { key: 'minutes', value: minutes, label: 'Minutes' },
    { key: 'seconds', value: seconds, label: 'Seconds' },
  ];

  return (
    <section
      className="rounded-card border border-border bg-card shadow-subtle overflow-hidden"
      data-testid="phase2-countdown"
      aria-label={`Phase 2 deadline${formatted ? `: ${formatted}` : ''}`}
    >
      <div className="flex flex-col gap-1 p-card-comfortable sm:p-card-roomy bg-muted/50">
        <span className="text-eyebrow-sm text-muted-foreground">
          Phase 2 Deadline
        </span>
        {formatted ? (
          <p
            className="font-display text-page-title text-foreground"
            data-testid="phase2-countdown-due"
          >
            {formatted}
          </p>
        ) : null}
      </div>
      <div className="border-t border-border p-card-comfortable sm:p-card-roomy">
        <div className="grid grid-cols-4 gap-3 sm:gap-6">
          {units.map((unit) => (
            <div key={unit.key} className="flex flex-col items-center text-center">
              <span
                data-testid={`phase2-countdown-${unit.key}`}
                className="font-display text-kpi sm:text-kpi-lg text-tertiary"
              >
                {String(unit.value).padStart(2, '0')}
              </span>
              <span className="mt-1 text-eyebrow-sm text-muted-foreground">
                {unit.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Phase2Checklist({
  profile,
}: {
  profile: ApplicantDashboardYearProfile | null;
}): JSX.Element | null {
  if (!profile) return null;

  let costumeSubmitted = false;
  try {
    if (profile.phase1Data?.generalBackground) {
      const bg = JSON.parse(profile.phase1Data.generalBackground);
      costumeSubmitted = !!bg?.costumeSubmitted;
    }
  } catch {
    // malformed JSON — treat as not submitted
  }

  const stageFormStarted = !!(
    profile.stageFormData &&
    (profile.stageFormData.stageName?.trim() ||
      profile.stageFormData.fitnessRoutine?.trim() ||
      profile.stageFormData.mostImportantLearning?.trim())
  );
  const interviewStarted = !!(
    profile.interviewInfoData &&
    (profile.interviewInfoData.nameAndTitle?.trim() ||
      profile.interviewInfoData.pageantAccomplishmentGoals?.trim())
  );
  const placementStarted =
    profile.placementQuestionsLocked ||
    !!(
      profile.placementQuestionsData &&
      (profile.placementQuestionsData.top15Response?.trim() ||
        profile.placementQuestionsData.interviewJudgesQuestion?.trim())
    );

  const items = [
    {
      label: 'State Costume Submission',
      to: '/portal/profile?section=costume',
      done: costumeSubmitted,
      icon: Shirt,
    },
    {
      label: 'Stage Form',
      to: '/portal/profile?section=stage-form',
      done: stageFormStarted,
      icon: FileText,
    },
    {
      label: 'Interview Info Sheet',
      to: '/portal/profile?section=interview-info',
      done: interviewStarted,
      icon: ClipboardList,
    },
    {
      label: 'Placement Questions',
      to: '/portal/profile?section=placement-questions',
      done: placementStarted,
      icon: HelpCircle,
    },
  ];

  return (
    <div
      className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
      data-testid="phase2-checklist"
    >
      {items.map((item) => (
        <Link
          key={item.label}
          to={item.to}
          className={cn(
            'flex items-center gap-3 rounded-card border p-3 min-h-[var(--touch-target-min)] no-underline transition-colors',
            item.done
              ? 'border-success/30 bg-success-muted/40 hover:bg-success-muted/60'
              : 'border-border bg-background hover:border-primary',
          )}
          data-testid={`phase2-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <item.icon
            className={cn(
              'w-4 h-4 shrink-0',
              item.done ? 'text-success-text' : 'text-muted-foreground',
            )}
          />
          <span className="text-eyebrow-sm text-foreground truncate">
            {item.label}
          </span>
        </Link>
      ))}
    </div>
  );
}

function UpcomingEventsPanel({
  events,
}: {
  events: Array<{
    id: string;
    title: string;
    date: string;
    time: string;
    location: string;
    type: string;
  }>;
}): JSX.Element {
  return (
    <div
      className="lg:col-span-2 bg-card border border-border rounded-card shadow-subtle p-6 sm:p-8"
      data-testid="upcoming-events-panel"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-display text-page-title text-foreground">
            Upcoming Events
          </h3>
        </div>
        <Link
          to="/portal/calendar"
          className="text-eyebrow-sm text-primary hover:text-foreground transition-colors no-underline touch-target"
        >
          View Calendar
        </Link>
      </div>
      {events.length === 0 ? (
        <p className="text-eyebrow-sm text-muted-foreground">
          No upcoming events scheduled.
        </p>
      ) : (
        <div className="space-y-4">
          {events.slice(0, 3).map((event) => (
            <Link
              key={event.id}
              to={`/portal/calendar?event=${event.id}&month=${event.date.slice(0, 7)}`}
              className="flex items-start gap-4 no-underline -mx-2 px-2 py-2 rounded transition-colors hover:bg-muted/40"
            >
              <div className="flex flex-col items-center min-w-[44px]">
                <span className="text-kpi-sm text-foreground">
                  {new Date(event.date + 'T00:00:00').getDate()}
                </span>
                <span className="text-eyebrow-sm text-muted-foreground">
                  {new Date(event.date + 'T00:00:00').toLocaleDateString(
                    'default',
                    { month: 'short' },
                  )}
                </span>
              </div>
              <div className="min-w-0">
                <p className="font-sans text-sm text-foreground font-medium truncate">
                  {event.title}
                </p>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <span className="text-eyebrow-sm text-muted-foreground inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {event.time}
                  </span>
                  {event.location ? (
                    <span className="text-eyebrow-sm text-muted-foreground inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {shortenAddress(event.location)}
                    </span>
                  ) : null}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function RecentActivityPanel({
  docCount,
  serviceHours,
  submissionStatus,
}: {
  docCount: number;
  serviceHours: number;
  submissionStatus?: string;
}): JSX.Element {
  // Recent activity surfaces three concrete state tiles rather than a
  // contrived audit log: documents uploaded, service hours logged,
  // and submission state. This keeps the panel grounded in what the
  // contestant actually has visibility into without inventing data.
  const activity = [
    {
      icon: FileText,
      label: 'Documents',
      value: docCount === 0 ? 'No uploads yet' : `${docCount} uploaded`,
      to: '/portal/documents',
    },
    {
      icon: Crown,
      label: 'Service Hours',
      value:
        serviceHours === 0
          ? 'No activity logged'
          : `${serviceHours.toLocaleString(undefined, {
              maximumFractionDigits: 1,
            })} hours`,
      to: '/portal/profile?section=service',
    },
    {
      icon: Sparkles,
      label: 'Application',
      value: submissionStatus
        ? formatStatus(submissionStatus)
        : 'Not Started',
      to: '/portal/profile',
    },
  ];

  return (
    <div
      className="bg-card border border-border rounded-card shadow-subtle p-6 sm:p-8"
      data-testid="recent-activity-panel"
    >
      <h3 className="font-display text-page-title text-foreground mb-5">
        Recent Activity
      </h3>
      <div className="space-y-3">
        {activity.map((row) => (
          <Link
            key={row.label}
            to={row.to}
            className="flex items-center gap-3 rounded-card -mx-2 px-2 py-2 no-underline hover:bg-muted/40 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground shrink-0">
              <row.icon className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-eyebrow-sm text-muted-foreground block">
                {row.label}
              </span>
              <span className="font-sans text-sm text-foreground">
                {row.value}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function formatStatus(status: string): string {
  return status
    .toLowerCase()
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

export default ApplicantDashboard;
